<template>
  <el-dialog
    :title="$t('member.tab.memberInfoEdit.title')"
    :visible="true"
    :close-on-click-modal="false"
    @close="$emit('close')"
  >
    <div v-loading="!loaded" class="profile-tab">
      <div v-if="loaded">
        <BaseElForm
          ref="form"
          :model="allFormData"
          label-position="top"
          class="form"
          :rules="formRules"
        >
          <BaseElFormItem :label="$t('member.tab.memberInfo.userStatus')" prop="memberStatus">
            <BaseElRadioGroup v-model="formData.memberStatus">
              <div class="flex flex-row gap-4 items-center">
                <BaseElRadio label="normal">{{ $t('member.tab.memberInfoEdit.statusNormal') }}</BaseElRadio>
                <BaseElRadio label="warning">
                  <span class="inline-flex items-center gap-1">
                    {{ $t('member.tab.memberInfoEdit.statusWarning') }}
                    <TipInfo :width="200">{{ $t('member.tab.memberInfoEdit.warningTooltip') }}</TipInfo>
                  </span>
                </BaseElRadio>
                <BaseElRadio label="blocked">
                  <span class="inline-flex items-center gap-1">
                    {{ $t('member.tab.memberInfoEdit.statusBlocked') }}
                    <TipInfo :width="200">{{ $t('member.tab.memberInfoEdit.blockedTooltip') }}</TipInfo>
                  </span>
                </BaseElRadio>
              </div>
            </BaseElRadioGroup>
          </BaseElFormItem>

          <BaseElFormItem v-if="useMemberOpentix" :label="$t('member.tab.memberInfoEdit.opentixBinding')">
            <div class="icon__vertical-align">
              <SVGIcon
                v-if="opentixBinded"
                icon="common-check"
                fill="var(--primary-100)"
                width="24"
                height="24"
              />
              <SVGIcon
                v-else
                icon="common-close"
                fill="var(--danger)"
                width="24"
                height="24"
              />
            </div>
          </BaseElFormItem>
          <!-- 新增：凱惠 POS 綁定 -->
          <BaseElFormItem v-if="useTKHSPC" v-featureKey="'planModule.tkhspc.enable'" :label="$t('member.tab.memberInfoEdit.tkhspcBinding')">
            <span>{{ member?.systemCustom?.tkhspcUid ? member.systemCustom.tkhspcUid : '-' }}</span>
          </BaseElFormItem>

          <BaseElFormItem :label="$t('member.tab.memberInfoEdit.loginMethod')" prop="memberUserAuth">
            <div class="icon__vertical-align">
              <span
                v-if="
                  !formData.User.AuthLine &&
                    !formData.User.AuthFB &&
                    !formData.User.AuthPhoneAccount
                "
              >-</span>
              <img
                v-if="formData.User.AuthLine"
                src="@/assets/member/icon-auth-line.svg"
                alt="Line auth icon"
              >
              <img
                v-if="formData.User.AuthFB"
                src="@/assets/member/icon-auth-facebook.svg"
                alt="Facebook auth icon"
              >
              <img
                v-if="formData.User.AuthPhoneAccount"
                src="@/assets/member/icon-auth-phone.svg"
                alt="Phone Account auth icon"
              >
            </div>
          </BaseElFormItem>

          <MemberLevelField v-if="showMemberLevel" :formData.sync="formData" :member="member" />

          <!-- <BaseElFormItem label="會員級別" prop="level">
            <BaseElSelect v-model="formData.level">
              <BaseElSelectOption
                v-for="(item, index) in levelList"
                :key="index"
                :label="item.name"
                :value="item.level"
              />
            </BaseElSelect>
          </BaseElFormItem> -->

          <BaseElFormItem :label="$t('member.tab.memberInfoEdit.phoneLabel')" prop="phone">
            <div>
              <InternationalPhoneInput
                :phone.sync="formData.phone"
                :phone-country.sync="formData.phoneCountry"
                :enableInternationalPhone="enableInternationalPhone"
                :disabledAll="true"
              />
              <div
                v-if="!formData.phone"
                class="cursor-pointer underline"
                style="color: var(--primary-100)"
                @click="showBindPhoneDialog = true"
              >
                {{ $t('member.tab.memberInfoEdit.bindPhone') }}
              </div>
              <div
                v-if="formData.phone"
                class="cursor-pointer underline"
                style="color: var(--danger)"
                @click="showDeBindPhoneDialog = true"
              >
                {{ $t('member.tab.memberInfoEdit.unbindPhone') }}
              </div>
            </div>
          </BaseElFormItem>

          <!-- 基本欄位 -->
          <BaseElFormItem
            v-for="field in baseUserSetting"
            :key="field.key"
            :label="field.name"
            :prop="field.key"
            :rules="getBaseSettingRules[field.key]"
          >
            <component
              :is="getBaseFieldComponent(field.type).is"
              v-model="formData[field.key]"
              :value="formData[field.key]"
              :name="field.key"
              :maxlength="30"
              show-word-limit
              :type="getBaseFieldComponent(field.type).nativeType || 'text'"
              :disabled="!field.adminEdit"
              :disabledAll="!field.adminEdit"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              :hideLabel="true"
              :year.sync="formData.birthday.year"
              :month.sync="formData.birthday.month"
              :date.sync="formData.birthday.date"
              :city.sync="formData.address.city"
              :area.sync="formData.address.area"
              :detail.sync="formData.address.detail"
              :placeholder="$t('member.tab.memberInfoEdit.validationPleaseEnter')"
              value-key="id"
              :autosize="{ minRows: 2, maxRows: 5 }"
              :targetMemberId="member.id"
              :fileId.sync="formDataCustom[field.key]"
            >
              <template v-if="!['radioSubField', 'checkboxSubField'].includes(field.type)">
                <BaseElSelectOption
                  v-for="item in field.enum || customeEnum[field.type]"
                  :key="item.id || item"
                  :label="item.label || item"
                  :value="item.value || item"
                />
              </template>
            </component>
          </BaseElFormItem>

          <!-- 自定義欄位 -->
          <BaseElFormItem
            v-for="field in customUserSetting"
            v-show="
              field.type !== 'companyDirectoryFirmTaxId' ||
                (showCompany && field.type === 'companyDirectoryFirmTaxId')
            "
            :key="field.key"
            :label="field.name"
            :prop="field.key"
            :rules="getCustomSettingRules[field.key]"
          >
            <component
              :is="getCustomFieldComponent(field.type).is"
              v-model="formDataCustom[field.key]"
              :name="field.key"
              :type="getCustomFieldComponent(field.type).nativeType || 'text'"
              :disabled="!field.adminEdit"
              :multiple="getCustomFieldComponent(field.type).multiple"
              :subFieldOptions="['radioSubField', 'checkboxSubField'].includes(field.type) ? field.subFieldOptions : undefined"
              :placeholder="$t('member.tab.memberInfoEdit.validationPleaseEnter')"
              value-key="id"
              :autosize="{ minRows: 2, maxRows: 5 }"
              :targetMemberId="member.id"
              :fileId.sync="formDataCustom[field.key]"
            >
              <template v-if="!['radioSubField', 'checkboxSubField'].includes(field.type)">
                <BaseElSelectOption
                  v-for="item in field.enum || customeEnum[field.type]"
                  :key="item.id || item"
                  :label="item.label || item"
                  :value="item.value || item"
                />
              </template>
            </component>
          </BaseElFormItem>
        </BaseElForm>

        <div slot="footer" class="flex justify-end">
          <BaseElButton plain @click="cancel">{{ $t('member.tab.memberInfoEdit.cancel') }}</BaseElButton>
          <BaseElButton type="primary" @click="confirm">{{ $t('member.tab.memberInfoEdit.save') }}</BaseElButton>
        </div>
      </div>
      <!-- ANCHOR 解除/綁定手機 Dialog -->
      <DeBindPhoneWarningDialog
        v-loading="isLoading"
        :show.sync="showDeBindPhoneDialog"
        :member="member"
        @debind="onDebindPhone"
      />
      <BindPhoneDialog
        v-loading="isLoading"
        :show.sync="showBindPhoneDialog"
        :member="member"
        :enableInternationalPhone="enableInternationalPhone"
        @bind="onBindPhone"
      />
    </div>
  </el-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import dayjs from '@/lib/dayjs'
import AddressInput from '@/components/Input/AddressInput'
import BirthDayInput from '@/components/Input/BirthDayInput.vue'
import InternationalPhoneInput from '@/components/Input/InternationalPhoneInput.vue'
import BindPhoneDialog from './BindPhoneDialog.vue'
import DeBindPhoneWarningDialog from './DeBindPhoneWarningDialog.vue'
import DebindAuthLineDialog from './DeBindAuthLineDialog.vue'
import MemberLevelField from './MemberLevelField.vue'
import RestrictedImageUpload from '@/components/RestrictedImageUpload'
// api
import TipInfo from '@/components/TipInfo.vue'
import { SimpleFindCompanyContactMenu } from '@/api/company/contactMenu'
import { UpdateMember } from '@/api/member'
import { DeleteAuthPhone, UpdateAuthPhone } from '@/api/auth'
// utils
import {
  phoneRules,
  noEmptyRules,
  emailRulesNoRequired,
  maxRules,
} from '@/validation/index'
import formUtils from '@/utils/form'
import AddressModel from '@/utils/address'
import { genderOptions } from '@/config/userInfoOptions'
import { DEFAULT_PHONE_COUNTRY } from '@/utils/internationalPhone'
import cities from '@/assets/data/cities'
import { get } from 'lodash'
import { useCompany } from '@/use/company'
import { defineComponent, ref, set, computed } from 'vue'

import { usePermissions } from '@/use/permissions'
import useDirty from '@/use/useDirty'
import { useMemberContext } from '@/use/useMemberContext'
import { t } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'MemberProfileEdit',
  components: {
    TipInfo,
    RestrictedImageUpload,
    InternationalPhoneInput,
    BindPhoneDialog,
    DeBindPhoneWarningDialog,
    DebindAuthLineDialog,
    AddressInput,
    BirthDayInput,
    MemberLevelField,
  },
  props: {
    member: { type: Object, required: true, default: () => {} },
    levelList: { type: Array, required: true, default: () => [] },
    baseUserSetting: { type: Array, required: true, default: () => [] },
    customUserSetting: { type: Array, required: true, default: () => [] },
    opentixBinded: Boolean,
    useMemberOpentix: Boolean,
  },

  setup () {
    const { useCompanyContactMenu, getShopContactMenuInfo, orgTaxId } =
      useCompany()
    const { memberTopInfo } = useMemberContext()

    const { checkAction } = usePermissions()

    const useTKHSPC = computed(() => checkAction('planModule.tkhspc.enable'))
    // 店家方案沒有會員等級（GIMMATEK 不提供）時不顯示等級欄位（CNCY-10766）
    const showMemberLevel = computed(() => checkAction('admin.memberLevelConfig.page'))

    const formData = ref({
      MemberAuthLine: false,
      memberStatus: 'normal',
      level: '',
      levelExpiredAt: null,
      levelNote: null,
      memberLevelEnable: true,
      User: {},
      custom: {},
      isLevelRemainFulfilled: false,
      recognizedTimeReset: null,
      recognizedTimeResetValue: null,
    })
    const { dirtyFields, startWatch } = useDirty(formData)

    const onFieldDataChange = (field, e) => {
      if (field.type === 'checkboxSubField' || field.type === 'radioSubField') {
        set(formData.value, field.key, e)
      }
    }

    return {
      dirtyFields,
      startWatch,
      formData,
      orgTaxId,
      getShopContactMenuInfo,
      useCompanyContactMenu,
      onFieldDataChange,
      useTKHSPC,
      showMemberLevel,
      memberTopInfo,
    }
  },

  data: () => ({
    loaded: false,
    isLoading: false,
    showDeBindPhoneDialog: false,
    showBindPhoneDialog: false,

    formDataCustom: {}, // 自定義欄位資料
    customeEnum: {
      gender: genderOptions.map(opt => ({ label: opt.name, value: opt.value })),
    },
    // ANCHOR data rules
    baseRequiredRules: {
      name: [noEmptyRules(t('member.tab.memberInfoEdit.validationPleaseEnter'))],
      email: [noEmptyRules(t('member.tab.memberInfoEdit.validationPleaseEnter'))],
      gender: [noEmptyRules(t('member.tab.memberInfoEdit.validationPleaseSelect'))],
      birthday: [noEmptyRules(t('member.tab.memberInfoEdit.validationPleaseSelect'))],
      'birthday.year': [noEmptyRules(t('member.tab.memberInfoEdit.validationPleaseSelect'))],
      'birthday.month': [noEmptyRules(t('member.tab.memberInfoEdit.validationPleaseSelect'))],
      'birthday.date': [noEmptyRules(t('member.tab.memberInfoEdit.validationPleaseSelect'))],
      address: [noEmptyRules(t('member.tab.memberInfoEdit.validationPleaseEnter'))],
    },
    baseAdvanceRules: {
      name: [maxRules(30)],
      email: [emailRulesNoRequired()],
      phone: [phoneRules()],
      gender: [],
      birthday: [],
      address: [],
    },
    // 定義固定欄位元件
    baseFieldsInput: {
      address: {
        is: AddressInput,
      },
      birthday: {
        is: 'BirthDayInput',
        placeholder: t('member.tab.memberInfoEdit.selectDate'),
        format: 'yyyy-MM-dd',
        'value-format': 'yyyy-MM-dd',
        nativeType: 'date',
      },
      gender: {
        is: 'el-select',
        options: genderOptions,
      },
    },
    // 定義自訂欄位元件
    customFieldsInput: {
      text: {
        is: 'el-input',
      },
      richtext: {
        is: 'el-input',
        nativeType: 'textarea',
      },
      radio: {
        is: 'el-select',
      },
      checkbox: {
        is: 'el-select',
        multiple: true,
      },
      radioSubField: {
        is: 'ParentChildSelect',
      },
      checkboxSubField: {
        is: 'ParentChildMultiSelect',
      },
      companyDirectoryFirmTaxId: {
        is: 'el-select',
        multiple: false,
      },
      restrictedImage: {
        is: 'RestrictedImageUpload',
      },
    },
  }),

  computed: {
    ...mapGetters(['orgId', 'shop', 'userPlanFeature', 'userFeatures']),
    shop () {
      return this.$store.getters.shop
    },
    getBaseFieldComponent () {
      return (fieldType) =>
        this.baseFieldsInput[fieldType] || { is: 'el-input' }
    },
    getCustomFieldComponent () {
      return (fieldType) =>
        this.customFieldsInput[fieldType] || { is: 'el-input' }
    },
    // ANCHOR Dynamic rules
    getBaseSettingRules () {
      return this.baseUserSetting.reduce((rules, field) => {
        if (!field.adminEdit) {
          return rules
        }
        if (field.required) {
          rules[field.key] = this.baseRequiredRules[field.key].concat(
            this.baseAdvanceRules[field.key],
          )
          return rules
        }
        rules[field.key] = this.baseAdvanceRules[field.key]
        return rules
      }, {})
    },
    getCustomSettingRules () {
      const rules = this.customUserSetting.reduce((rules, field) => {
        if (!field.adminEdit) {
          return rules
        }
        if (field.required) {
          if (field.type === 'radioSubField') {
            // 自訂驗證: parentValue 與 childValue 皆不可為空
            rules[field.key] = [
              {
                required: true,
                validator: (rule, value, callback) => {
                  if (!value || !value.parentValue || !value.childValue) {
                    callback(new Error(t('member.tab.memberInfoEdit.validationPleaseSelectOption')))
                  } else {
                    callback()
                  }
                },
                trigger: 'change',
              },
            ]
          } else {
            rules[field.key] = [noEmptyRules(t('member.tab.memberInfoEdit.validationRequired'))]
          }
        }
        return rules
      }, {})
      console.log('getCustomSettingRules', rules)
      return rules
    },
    compactFormData () {
      const formData = { ...this.formData }
      if (!formData.level) {
        formData.level = 0
      }
      // 不顯示等級時不送等級欄位，避免後端當成手動異動等級
      if (!this.showMemberLevel) {
        delete formData.level
        delete formData.levelExpiredAt
        delete formData.levelNote
        delete formData.isLevelRemainFulfilled
      }
      formData.birthday = this.compactBirthday(formData)
      formData.address = this.compactAddress(formData)
      return formData
    },
    allFormData () {
      return { ...this.compactFormData, ...this.formDataCustom }
    },
    enableInternationalPhone () {
      return get(this.$store.state.org, 'enableInternationalPhone', false)
    },
    showCompany () {
      return this.useCompanyContactMenu && this.orgTaxId(this.customUserSetting)
    },
    shouldSubmitRecognizedTimeReset () {
      return this.dirtyFields('recognizedTimeReset') || this.dirtyFields('recognizedTimeResetValue') || this.dirtyFields('level')
    },
    formRules () {
      const rule = {
        level: [noEmptyRules()],
      }
      if (!this.formData.forever) {
        rule.levelExpiredAt = [noEmptyRules()]
      }
      // Only require recognizedTimeReset if level is changed AND set to a specific level (not "no level")
      if (this.dirtyFields('level') && this.formData.level) {
        rule.recognizedTimeReset = [noEmptyRules(t('member.tab.memberInfoEdit.validationPleaseSelect'))]
      }
      if (this.shouldSubmitRecognizedTimeReset && this.formData.recognizedTimeReset === 'fromDaysBeforeUpgrade') {
        rule.recognizedTimeResetValue = [noEmptyRules()]
      }
      return rule
    },
  },

  watch: {
    member: {
      handler () {
        this.syncForm()
        this.startWatch()
      },
      deep: true,
    },
    'formData.level' () {
      this.$nextTick(() => {
        setTimeout(() => {
          formUtils.clearValidate(this.$refs.form, 'recognizedTimeReset')
        }, 50)
      })
    },
  },

  async mounted () {
    this.syncForm()
    this.startWatch()
    await this.getShopContactMenuInfo({ shopId: this.shop })
    if (!this.useCompanyContactMenu || !this.orgTaxId(this.customUserSetting)) {
      return
    }
    this.simpleFindCompanyContactMenu()
  },

  methods: {
    async simpleFindCompanyContactMenu () {
      let start = 0
      const limit = 10
      const data = []
      let temp = []
      const [res, err] = await SimpleFindCompanyContactMenu({
        shopId: this.shop,
        start,
        limit,
      })
      if (err) {
        this.$message.error(err)
        return
      }
      start += res.length
      temp = res
      data.push(...res)

      while (temp.length === limit) {
        const [res, err] = await SimpleFindCompanyContactMenu({
          shopId: this.shop,
          start,
          limit,
        })
        if (err) {
          this.$message.error(err)
          return
        }
        start += res.length
        temp = res
        data.push(...res)
      }

      this.$set(
        this.customeEnum,
        'companyDirectoryFirmTaxId',
        data.map((i) => ({
          label: i.name,
          id: i.id,
          value: i.taxId,
        })),
      )
    },
    compactBirthday (data) {
      const year = get(data, 'birthday.year')
      const month = get(data, 'birthday.month')
      const date = get(data, 'birthday.date')
      if (!year || !month || !date) return null
      return dayjs(`${year}-${month}-${date}`).toDate()
    },
    compactAddress (data) {
      const city = get(data, 'address.city')
      const area = get(data, 'address.area')
      const detail = get(data, 'address.detail')
      return city + area + detail
    },
    async confirm () {
      if (!(await formUtils.checkForm(this.$refs.form))) return
      const success = await this.updateMember()
      if (!success) return
      this.$emit('refresh')
      this.$emit('close')
    },

    cancel () {
      this.$emit('cancel')
      this.$emit('close')
    },

    getRecognizedTimeResetPayload () {
      if (!this.shouldSubmitRecognizedTimeReset || !this.formData.recognizedTimeReset) {
        return {
          recognizedTimeReset: undefined,
          recognizedTimeResetValue: undefined,
        }
      }

      return {
        recognizedTimeReset: this.formData.recognizedTimeReset,
        recognizedTimeResetValue: this.formData.recognizedTimeReset === 'fromDaysBeforeUpgrade'
          ? this.formData.recognizedTimeResetValue
          : undefined,
      }
    },

    async updateMember () {
      try {
        await UpdateMember({
          shopId: this.shop,
          ...this.compactFormData,
          phone: undefined,
          gender: this.formData.gender || null,
          custom: this.formDataCustom,
          ...this.getRecognizedTimeResetPayload(),
        })
        this.$message.success(t('member.tab.memberInfoEdit.updateSuccess'))
        return true
      } catch (error) {
        console.log(error)
        this.$message.error({
          message: error || error.message,
        })
        return false
      }
    },
    syncForm () {
      this.formData = { ...this.member }
      // 舊欄位向後相容：若 memberStatus 未設定，則根據 isBlock 導出
      if (!this.formData.memberStatus) {
        this.formData.memberStatus = this.member.isBlock ? 'blocked' : 'normal'
      }

      this.formData.phone = get(this.memberTopInfo, 'memberUserInfo.phone')
      this.formData.phoneCountry =
        get(this.memberTopInfo, 'memberUserInfo.phoneCountry') ||
        get(this.member, 'UserInfo.phoneCountry') ||
        DEFAULT_PHONE_COUNTRY

      if (this.formData.birthday) {
        const birthday = dayjs(this.formData.birthday).format('YYYY-MM-DD').split('-')
        this.$set(this.formData, 'birthday', {})
        this.$set(this.formData.birthday, 'year', Number(birthday[0]))
        this.$set(this.formData.birthday, 'month', Number(birthday[1]))
        this.$set(this.formData.birthday, 'date', Number(birthday[2]))
      }
      if (!this.formData.birthday) {
        this.$set(this.formData, 'birthday', {
          year: '',
          month: '',
          date: '',
        })
      }

      if (this.formData.address) {
        const addressParser = new AddressModel(cities)
        const address = addressParser.parse(this.formData.address)
        this.$set(this.formData, 'address', {})
        this.$set(this.formData.address, 'city', address.city)
        this.$set(this.formData.address, 'area', address.area)
        this.$set(this.formData.address, 'detail', address.address)
      }
      if (!this.formData.address) {
        this.$set(this.formData, 'address', {})
        this.$set(this.formData.address, 'city', '')
        this.$set(this.formData.address, 'area', '')
        this.$set(this.formData.address, 'detail', '')
      }
      this.formDataCustom = { ...this.member.custom }

      // normalize recognizedTimeReset fields to null so the dirty-detection snapshot
      // is consistent and undefined→null transitions (e.g. Element UI Select
      // normalising an unmatched value) do not falsely trigger the field as dirty
      if (this.formData.recognizedTimeReset === undefined) {
        this.formData.recognizedTimeReset = null
      }
      if (this.formData.recognizedTimeResetValue === undefined) {
        this.formData.recognizedTimeResetValue = null
      }

      this.loaded = true
    },
    // ANCHOR onDebindPhone
    async onDebindPhone () {
      this.isLoading = true
      const [, err] = await DeleteAuthPhone({
        shopId: this.shop,
        id: this.member.id,
      })
      this.isLoading = false

      if (err) return this.$message.error(err)

      this.formData.phone = ''
      this.formData.phoneCountry = DEFAULT_PHONE_COUNTRY
      this.$message.success(t('member.tab.memberInfo.unbindSuccess'))
      this.showDeBindPhoneDialog = false
      this.$emit('refresh')
    },
    // ANCHOR onDebindPhone
    async onBindPhone ({ phone, phoneCountryCode, phoneCountry }) {
      this.isLoading = true
      const [, err] = await UpdateAuthPhone({
        shopId: this.shop,
        id: this.member.id,
        phone,
        phoneCountryCode,
        phoneCountry,
      })
      this.isLoading = false

      if (err) return this.$message.error(err)

      this.formData.phone = phone
      this.formData.phoneCountry = phoneCountry
      this.$message.success(t('member.tab.memberInfo.updateSuccess'))
      this.showBindPhoneDialog = false
      this.$emit('refresh')
    },
  },
})
</script>

<style scoped lang="postcss">
::v-deep .el-select .el-input {
  @apply h-full;
}
::v-deep .el-tag--info {
  @apply h-full break-all;
  white-space: break-spaces;
}
::v-deep .el-select__tags {
  @apply flex flex-wrap;
}

.bind-phone-btn {
  @apply cursor-pointer text-left;
  @apply absolute left-[300px] top-0;
}

.icon__vertical-align {
  @apply flex gap-x-[4px];
  @apply mt-[10px];
}

.el-date-editor {
  @apply w-full;
}
</style>
